import { SquarePen, Trash2 } from 'lucide-react';
import { memo } from 'react';

import type { MessageTableRowProps } from '@/types/predefined-messages';

import { RecipientBadges } from '@/components/common/recipient-badges';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { areMessageTableRowPropsEqual } from '@/lib/helpers/message-table-row-props-equal';
import { collapseRolesToAll, mapRoleIdentifiersToShortNames } from '@/lib/helpers/role-mapper';
import { cn } from '@/lib/utils';
import { ALL_AVAILABLE_TO_ROLE_KEYS } from '@/utils/constants/roles';

function MessageTableRowComponent({
  filteredMessage,
  categoryNames,
  rowRef,
  dataIndex,
  onEdit,
  onDelete,
  className,
}: MessageTableRowProps) {
  const { message, predefinedMessageCategoryId, availableToIds, recipientIds, prioritized } =
    filteredMessage;

  const availableToShortNames = mapRoleIdentifiersToShortNames(
    collapseRolesToAll(availableToIds, ALL_AVAILABLE_TO_ROLE_KEYS)
  )
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

  return (
    <TableRow
      ref={rowRef}
      data-index={dataIndex}
      className={cn('[&_td]:align-middle', 'box-border h-14', className)}
    >
      <TableCell className="truncate px-5">{message}</TableCell>
      <TableCell className="truncate">
        {categoryNames[predefinedMessageCategoryId] ?? predefinedMessageCategoryId}
      </TableCell>
      <TableCell className="truncate">{availableToShortNames}</TableCell>
      <TableCell className="truncate">
        <RecipientBadges recipientIds={recipientIds} prioritized={prioritized} />
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Edit"
            onClick={() => onEdit?.(filteredMessage)}
          >
            <SquarePen className="text-muted-foreground" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Delete"
            onClick={() => onDelete?.(filteredMessage)}
          >
            <Trash2 className="text-muted-foreground" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export const MessageTableRow = memo(MessageTableRowComponent, areMessageTableRowPropsEqual);
